import { type RefObject, type UIEvent, useCallback, useLayoutEffect, useRef } from 'react';

interface UseScrollRestorationParams {
  viewType: string;
  wrapperRef: RefObject<HTMLDivElement | null>;
  isBackNavigation: RefObject<boolean>;
}

const MAX_HISTORY_SIZE = 5;
const RADIX_VIEWPORT_SELECTOR = '[data-radix-scroll-area-viewport]';

const useScrollRestoration = ({
  viewType,
  wrapperRef,
  isBackNavigation,
}: UseScrollRestorationParams) => {
  const scrollPositions = useRef<Map<string, number>>(new Map());
  const currentScrollValue = useRef<number>(0);

  const prevViewType = useRef<string>(viewType);

  if (prevViewType.current !== viewType) {
    const prevKey = prevViewType.current;
    const history = scrollPositions.current;

    if (isBackNavigation.current) {
      history.delete(prevKey);
      isBackNavigation.current = false;
    } else {
      history.set(prevKey, currentScrollValue.current);

      if (history.size > MAX_HISTORY_SIZE) {
        const firstKey = history.keys().next().value;

        if (firstKey) {
          history.delete(firstKey);
        }
      }
    }

    prevViewType.current = viewType;
    currentScrollValue.current = 0;
  }

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    const viewport = wrapper.querySelector<HTMLElement>(RADIX_VIEWPORT_SELECTOR);

    if (!viewport) {
      return;
    }

    const savedPosition = scrollPositions.current.get(viewType) ?? 0;

    currentScrollValue.current = savedPosition;

    const attemptRestore = (): boolean => {
      if (Math.abs(viewport.scrollTop - savedPosition) <= 1) {
        return true;
      }

      viewport.scrollTop = savedPosition;

      return Math.abs(viewport.scrollTop - savedPosition) <= 1;
    };

    attemptRestore();

    if (savedPosition > 0 && Math.abs(viewport.scrollTop - savedPosition) > 1) {
      const targetNode = viewport.children[0] || viewport;

      const resizeObserver = new ResizeObserver(() => {
        const success = attemptRestore();

        if (success) {
          resizeObserver.disconnect();
        }
      });

      resizeObserver.observe(targetNode);

      return () => resizeObserver.disconnect();
    }
  }, [viewType, wrapperRef]);

  const handleScrollCapture = useCallback((e: UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    if (target?.hasAttribute('data-radix-scroll-area-viewport')) {
      currentScrollValue.current = target.scrollTop;
    }
  }, []);

  return {
    handleScrollCapture,
  };
};

export default useScrollRestoration;
